import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import {
  Card,
  Container,
  Row,
  Col,
  Alert,
  Button,
  Form,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { handleIsActive } from "../util/common";
import Modal from "react-bootstrap/Modal";
import {
  addNewFacility,
  fetchFacilityById,
  updateFacilityRecord,
} from "../services/sendingFacilities-service";
import { fetchCodeSetTable } from "../services/referenceTable-service";
import { S3_URL } from "../services/Constants";
import _ from "lodash";
import moment from "moment";
import { useContext } from "react";
import UserContext from "../context/user-context";
import { componentIsEditable, validateAndSanitizeUrl } from "../util/common";
import {
  SFAFSF,
  SFUFNPIF,
  SFUFNPIFB,
  SFUFCLIAF,
  SFUFCLIAB,
  SFUFOIDF,
  SFUFOIDFB,
  SFUFFIDF,
} from "../util/component-codes";
import { isDefinedAndNotEmpty } from "../util/common";

const FacilityForm = () => {
  const [form, setForm] = useState({});
  const [validated, setValidated] = useState(false);
  const [alertMessage, setAlertMessage] = useState();
  const [statesData, setStatesData] = useState([]);
  const [isNpiFieldBlank, setIsNpiFieldBlank] = useState(false);
  const [isCLIAFieldBlank, setIsCLIAFieldBlank] = useState(false);
  const [isOIDFieldBlank, setIsOIDFieldBlank] = useState(false);
  const [
    showFacilityIdChangeWarningModal,
    setShowFacilityIdChangeWarningModal,
  ] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [user] = useContext(UserContext);

  const handleRemoveWarningModalClose = () =>
    setShowFacilityIdChangeWarningModal(false);

  const setField = (field, value) => {
    setForm({
      ...form,
      [field]: value,
    });
  };

  const cancelForm = () => {
    if (location?.state?.from === "unknownFacilityErrorQueue") {
      navigate("/unknownFacilities",  {
        state: {
          filter: location?.state?.filter,
          pageNo: location?.state?.pageNo,  
        },
      });
    } else {
      navigate("/facilities",  {
        state: {
          filter: location?.state?.filter,
          pageNo: location?.state?.pageNo,  
        },
      });
    }
  };

  const handleBackRouteMessage = () => {
    let message = "";
    let backTo = "";
    if (location?.state?.from === "unknownFacilityErrorQueue") {
      message = "Back to Unknown Facility Queue";
      backTo = "/unknownFacilities";
    } else {
      message = "Back to Sending Facility Table";
      backTo = "/facilities";
    }
    return <Link to={backTo} className="linkStyle"   state={{
      filter: location?.state?.filter,
      pageNo: location?.state?.pageNo,
    }}>{message}</Link>;
  };

  const showTitle = () => {
    let message = "Add New Sending Facility";
    if (location?.state?.action === "update") {
      message = "Update Sending Facility";
    }
    return message;
  };

  const showSubtitle = () => {
    let message = "Add a New Sending Facility";
    if (location?.state?.action === "update") {
      message = "Update the details of a Sending Facility";
    }
    return message;
  };

  const getStateDropdownValues = async () => {
    let response = await fetchCodeSetTable("State");
    if (response.ok) {
      let data = await response.json();
      setStatesData(data.refTableValues);
    }
  };

  const addFacility = async (newPayload) => {
    let data = await addNewFacility(newPayload);
    if (data.sendingFacilityId != null) {
      return { isAdded: true };
    } else {
      return { isAdded: false, message: data?.description};
    }
  };

  const updateFacility = async (newPayload) => {
    let payload = {
      ...newPayload,
      sendingFacilityId: location.state.selectedRow.sendingFacilityId,
    };
    let data = await updateFacilityRecord(payload);
    if (data.sendingFacilityId != null) {
      return { isUpdated: true };
    } else {
      return { isUpdated: false, message: data?.description};
    }
  };

  const getFacilityById = async () => {
    let sendingFacilityId = location.state.selectedRow.sendingFacilityId;
    let response = await fetchFacilityById(sendingFacilityId);
    if (response.ok) {
      let data = await response.json();
      if (data != null) {
        const parsedData = {
          sendingFacilityName: data?.sendingFacilityName,
          parentOrganization: data?.parentOrganization,
          sendingFacilityIdentifier: data?.sendingFacilityIdentifier,
          sendingFacilityType: data?.sendingFacilityType,
          clia: data?.clia,
          oid: data?.oid,
          npi: data?.npi,
          dataType: data?.dataType,
          dateActivated: data?.dateActivated,
          dateDeactivated: data?.dateDeactivated,
          plannedDate: data?.plannedDate,
          isActive: data?.isActive,
          addressId: data?.address?.addressId,
          addressLine1: data?.address?.addressLine1,
          addressLine2: data?.address?.addressLine2,
          city: data?.address?.city,
          state: data?.address?.state,
          zip: data?.address?.zip,
          phoneNo: data?.address?.phoneNo,
          submissionPathway: data?.submissionPathway,
          emr: data?.emr,
          lis: data?.lis,
          vendorName: data?.vendorName,
          vendorSoftware: data?.vendorSoftware,
          comments: data?.comments,
        };
        if (data?.npi == null || data?.npi === "") {
          setIsNpiFieldBlank(true);
        }
        if (data?.clia == null || data?.clia === "") {
          setIsCLIAFieldBlank(true);
        }
        if (data?.oid == null || data?.oid === "") {
          setIsOIDFieldBlank(true);
        }
        setForm(parsedData);
        setValidated(false);
      }
    } else {
      let error = await response.json();
      setAlertMessage(error?.description);
    }
  };

  const formSubmitPayload = () => {
    const newPayload = {
      sendingFacilityName: form.sendingFacilityName,
      parentOrganization: form.parentOrganization,
      sendingFacilityIdentifier: form.sendingFacilityIdentifier,
      sendingFacilityType: form.sendingFacilityType,
      clia: form.clia,
      oid: form.oid,
      npi: form.npi,
      dataType: form.dataType,
      dateActivated: form.dateActivated,
      dateDeactivated: form.dateDeactivated,
      plannedDate: form.plannedDate,
      isActive: form.isActive,
      address: {
        addressId: form.addressId,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        city: form.city,
        state: form.state,
        zip: form.zip,
        phoneNo: form.phoneNo,
      },
      submissionPathway: form.submissionPathway,
      emr: form.emr,
      lis: form.lis,
      vendorName: form.vendorName,
      vendorSoftware: form.vendorSoftware,
      comments: form.comments,
    };
    return newPayload;
  };

  const handleSubmmitChangesToFacilityId = async () => {
    let payload = formSubmitPayload();
    let response = await updateFacility(payload);
    navigate("/facilities", {
      state: { isUpdated: response.isUpdated, message: response?.message , 
        filter: location?.state?.filter,
        pageNo: location?.state?.pageNo,  }
    });
  };

  const handleSubmmit = async (event) => {
    event.preventDefault();
    setValidated(true);
    const newPayload = formSubmitPayload();
    if (event.currentTarget.checkValidity() === true) {
      let route = "/facilities";
      if (location?.state?.from === "unknownFacilityErrorQueue") {
        route = "/unknownFacilities";
      }
      if (location?.state?.selectedRow?.sendingFacilityId != null) {
        if (
          location?.state?.selectedRow?.sendingFacilityIdentifier !==
          form?.sendingFacilityIdentifier
        ) {
          setShowFacilityIdChangeWarningModal(true);
        } else {
          let response = await updateFacility(newPayload);
          navigate("/facilities", {
            state: { isUpdated: response.isUpdated, message: response?.message },
          });
        }
      } else {
        let response = await addFacility(newPayload);
        navigate(route, {
          state: { isAdded: response.isAdded, message: response?.message },
        });
      }
    }
  };

  useEffect(() => {
    location?.state?.selectedRow?.sendingFacilityId != null &&
      getFacilityById();
  }, []);

  useEffect(() => {
    getStateDropdownValues();
  }, []);

  const changeActiveAndDateState = (value) => {
    if (value === "Active" && form?.dateActivated == null) {
      setForm({
        ...form,
        dateActivated: moment().format("yyyy-MM-DD"),
        isActive: true,
      });
    } else if (value === "Active") {
      setForm({
        ...form,
        isActive: true,
      });
    } else if (value === "Inactive") {
      setForm({
        ...form,
        dateDeactivated: moment().format("yyyy-MM-DD"),
        isActive: false,
      });
    }
  };

  useEffect(() => {
    if (
      !componentIsEditable(SFAFSF, user) &&
      location?.state?.action !== "update"
    ) {
      setField("isActive", false);
    }
  }, []);

  const fieldIsDissabled = (isBlank, fieldCode, fieldIsBlankCode) => {
    const IsUpdateLocation = location?.state?.action === "update";
    const isFieldBlank = isBlank;
    if (componentIsEditable(fieldCode, user)) {
      if (
        componentIsEditable(fieldIsBlankCode, user) &&
        IsUpdateLocation &&
        isFieldBlank
      ) {
        return false;
      } else if (
        !componentIsEditable(fieldIsBlankCode, user) &&
        IsUpdateLocation &&
        isFieldBlank
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      if (
        componentIsEditable(fieldIsBlankCode, user) &&
        IsUpdateLocation &&
        isFieldBlank
      ) {
        return false;
      } else if (
        componentIsEditable(fieldIsBlankCode, user) &&
        IsUpdateLocation &&
        !isFieldBlank
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  return (
    <>
      <Container fluid className="errorMessageContainer">
        <Row className="tableTitle generalFont">{showTitle()}</Row>
        <Row
          className="tableSubtitle generalFont"
          style={{ width: "95%", paddingRight: "30px" }}
        >
          <Col style={{ paddingLeft: "0px" }}>{showSubtitle()}</Col>
          <Col style={{ display: "flex", justifyContent: "end" }}>
            {handleBackRouteMessage()}
          </Col>
        </Row>
        {alertMessage && (
          <Alert
            style={{ marginRight: "175px" }}
            variant={"danger"}
            show={alertMessage !== null && alertMessage !== "undefined"}
            onClose={() => {
              setAlertMessage();
            }}
            dismissible
          >
            {alertMessage}
          </Alert>
        )}
        <Row style={{ marginRight: "54px", marginTop: "10px" }}>
          <Box
            sx={{
              width: "100%",
              typography: "body1",
              paddingLeft: "0px",
              paddingRight: "0px",
            }}
          >
            <Form onSubmit={handleSubmmit} noValidate validated={validated}>
              <Card className="card">
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <div className="cardBody" style={{ marginBottom: "40px" }}>
                    <img
                      src={`${validateAndSanitizeUrl(S3_URL)}rules-details-icon.png`}
                      style={{
                        width: "20px",
                        height: "18px",
                        marginRight: "15px",
                      }}
                      alt=""
                    ></img>
                    Sending Facility Details
                  </div>
                  <Col>
                    <Form.Group
                      className="mb-3"
                      controlId="sendingFacilityName"
                    >
                      <React.Fragment>
                        <Form.Label className="modalLabel generalFont">
                          Facility Name{" "}
                          <i className="text-danger">(required)</i>
                        </Form.Label>
                      </React.Fragment>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={250}
                        placeholder="Hint Text"
                        required
                        value={form.sendingFacilityName}
                        onChange={(e) =>
                          setField("sendingFacilityName", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="parentOrganization">
                      <Form.Label className="modalLabel generalFont">
                        Parent Organization
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={250}
                        placeholder="Hint Text"
                        value={form.parentOrganization}
                        onChange={(e) =>
                          setField("parentOrganization", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <Col>
                    <Form.Group
                      className="mb-3"
                      controlId="sendingFacilityIdentifier"
                    >
                      <Form.Label className="modalLabel generalFont">
                        Facility ID <i className="text-danger">(required)</i>
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        maxLength={50}
                        type="text"
                        disabled={
                          !componentIsEditable(SFUFFIDF, user) &&
                          location?.state?.action === "update"
                        }
                        placeholder="Hint Text"
                        required
                        value={form.sendingFacilityIdentifier}
                        onChange={(e) =>
                          setField("sendingFacilityIdentifier", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group
                      className="mb-3"
                      controlId="sendingFacilityType"
                    >
                      <Form.Label className="modalLabel generalFont">
                        Facility Type
                      </Form.Label>
                      <Form.Select
                        className="modalInput generalFont"
                        aria-label="Choose Item"
                        value={form.sendingFacilityType}
                        onChange={(e) =>
                          setField("sendingFacilityType", e.target.value)
                        }
                      >
                        <option hidden value="">
                          Choose Item
                        </option>
                        <option value="Lab">Lab</option>
                        <option value="Facility">Facility</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <hr></hr>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <Col>
                    <Form.Group className="mb-3" controlId="clia">
                      <Form.Label className="modalLabel generalFont">
                        CLIA{" "}
                        {isDefinedAndNotEmpty(form.clia) &&
                          form?.clia?.length < 9 && (
                            <i className="text-danger">
                              (CLIA field must be at least 9 characters long)
                            </i>
                          )}
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        disabled={fieldIsDissabled(
                          isCLIAFieldBlank,
                          SFUFCLIAF,
                          SFUFCLIAB
                        )}
                        pattern="[a-zA-Z0-9]{9,}"
                        maxLength={15}
                        placeholder="Hint Text"
                        value={form.clia}
                        onChange={(e) =>
                          e.target.value === ""
                            ? setField("clia", null)
                            : setField("clia", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="oid">
                      <Form.Label className="modalLabel generalFont">
                        OID
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        disabled={fieldIsDissabled(
                          isOIDFieldBlank,
                          SFUFOIDF,
                          SFUFOIDFB
                        )}
                        maxLength={50}
                        placeholder="Hint Text"
                        value={form.oid}
                        onChange={(e) => setField("oid", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <Col>
                    <Form.Group className="mb-3" controlId="npi">
                      <Form.Label className="modalLabel generalFont">
                        NPI{" "}
                        {isDefinedAndNotEmpty(form.npi) &&
                          form?.npi?.length < 9 && (
                            <i className="text-danger">
                              (NPI field must be at least 9 characters long)
                            </i>
                          )}
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        disabled={fieldIsDissabled(
                          isNpiFieldBlank,
                          SFUFNPIF,
                          SFUFNPIFB
                        )}
                        pattern="[a-zA-Z0-9]{9,}"
                        maxLength={15}
                        placeholder="Hint Text"
                        value={form.npi}
                        onChange={(e) =>
                          e.target.value === ""
                            ? setField("npi", null)
                            : setField("npi", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="dataType">
                      <Form.Label className="modalLabel generalFont">
                        Data Type
                      </Form.Label>
                      <Form.Select
                        className="modalInput generalFont"
                        aria-label="Choose Item"
                        value={form.dataType}
                        onChange={(e) => setField("dataType", e.target.value)}
                      >
                        <option hidden value="">
                          Choose Item
                        </option>
                        <option value="ELR">ELR</option>
                        <option value="ECR">ECR</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <hr></hr>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <Col>
                    <Form.Group className="mb-3" controlId="dateActivated">
                      <Form.Label className="modalLabel generalFont">
                        Date Activated
                      </Form.Label>
                      <Form.Control
                        disabled
                        className="modalInput generalFont"
                        type="date"
                        placeholder="Hint Text"
                        value={form.dateActivated}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="dateDeactivated">
                      <Form.Label className="modalLabel generalFont">
                        Date Deactivated
                      </Form.Label>
                      <Form.Control
                        disabled
                        className="modalInput generalFont"
                        type="date"
                        placeholder="Hint Text"
                        value={form.dateDeactivated}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <Col>
                    <Form.Group className="mb-3" controlId="plannedDate">
                      <Form.Label className="modalLabel generalFont">
                        Date Planned
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="date"
                        placeholder="Hint Text"
                        value={form.plannedDate}
                        onChange={(e) =>
                          setField("plannedDate", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="isActive">
                      <Form.Label className="modalLabel generalFont">
                        Status <i className="text-danger">(required)</i>
                      </Form.Label>
                      <Form.Select
                        required
                        className="modalInput generalFont"
                        aria-label="Choose Item"
                        disabled={
                          !componentIsEditable(SFAFSF, user) &&
                          location?.state?.action !== "update"
                        }
                        value={
                          componentIsEditable(SFAFSF, user) ||
                          (!componentIsEditable(SFAFSF, user) &&
                            location?.state?.action === "update")
                            ? handleIsActive(form.isActive)
                            : handleIsActive(false)
                        }
                        onChange={(e) =>
                          changeActiveAndDateState(e.target.value)
                        }
                      >
                        <option hidden value="">
                          Choose Item
                        </option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <div className="cardBody">Address Details</div>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <Col>
                    <Form.Group className="mb-3" controlId="addressLine1">
                      <Form.Label className="modalLabel generalFont">
                        Street Address 1
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={255}
                        placeholder="Hint Text"
                        value={form.addressLine1}
                        onChange={(e) =>
                          setField("addressLine1", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="addressLine2">
                      <Form.Label className="modalLabel generalFont">
                        Street Address 2
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={255}
                        placeholder="Hint Text"
                        value={form.addressLine2}
                        onChange={(e) =>
                          setField("addressLine2", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <Col>
                    <Form.Group className="mb-3" controlId="city">
                      <Form.Label className="modalLabel generalFont">
                        City
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={100}
                        placeholder="Hint Text"
                        value={form.city}
                        onChange={(e) => setField("city", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="state">
                      <Form.Label className="modalLabel generalFont">
                        State
                      </Form.Label>
                      <Form.Select
                        className="modalInput generalFont"
                        aria-label="Choose Item"
                        value={form.state}
                        onChange={(e) => setField("state", e.target.value)}
                      >
                        <option hidden value="">
                          Choose Item
                        </option>
                        {_.sortBy(statesData, [
                          "reference_Code_description",
                        ]).map((entry) => {
                          return (
                            <option
                              key={entry.referenceTableValueId}
                              value={entry.reference_Code_description}
                            >
                              {entry.reference_Code_description}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="zip">
                      <Form.Label className="modalLabel generalFont">
                        Zip Code
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={15}
                        placeholder="Hint Text"
                        value={form.zip}
                        onChange={(e) => setField("zip", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="phoneNo">
                      <Form.Label className="modalLabel generalFont">
                        Phone Number
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={20}
                        placeholder="Hint Text"
                        value={form.phoneNo}
                        onChange={(e) => setField("phoneNo", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row style={{ padding: "40px 40px 40px 40px" }}>
                  <hr></hr>
                </Row>
                <Row style={{ paddingLeft: "40px" }}>
                  <div className="cardBody">Additional Information</div>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <Col>
                    <Form.Group className="mb-3" controlId="submissionPathway">
                      <Form.Label className="modalLabel generalFont">
                        Submission Pathway
                      </Form.Label>
                      <Form.Select
                        className="modalInput generalFont"
                        aria-label="Choose Item"
                        value={form.submissionPathway}
                        onChange={(e) =>
                          setField("submissionPathway", e.target.value)
                        }
                      >
                        <option hidden value="">
                          Choose Item
                        </option>
                        <option value="Site>IDPH">Site&gt;IDPH</option>
                        <option value="Site>RCKMS>IDPH">
                          Site&gt;RCKMS&gt;IDPH
                        </option>
                        <option value="Site>APHL>IDPH">
                          Site&gt;APHL&gt;IDPH
                        </option>
                        <option value="Site>PHINMS>IDPH">
                          Site&gt;PHINMS&gt;IDPH
                        </option>
                        <option value="Site>Broker>IDPH">
                          Site&gt;Broker&gt;IDPH
                        </option>
                        <option value="Site>Vendor>IDPH">
                          Site&gt;Vendor&gt;IDPH
                        </option>
                        <option value="Site->Vendor->RCKMS->IDPH">
                          Site&gt;Vendor&gt;RCKMS&gt;IDPH
                        </option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="emr">
                      <Form.Label className="modalLabel generalFont">
                        EMR
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={50}
                        placeholder="Hint Text"
                        value={form.emr}
                        onChange={(e) => setField("emr", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3" controlId="lis">
                      <Form.Label className="modalLabel generalFont">
                        LIS
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={50}
                        placeholder="Hint Text"
                        value={form.lis}
                        onChange={(e) => setField("lis", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row style={{ padding: "40px 40px 0 40px" }}>
                  <Col sm={4}>
                    <Form.Group className="mb-3" controlId="vendorName">
                      <Form.Label className="modalLabel generalFont">
                        Vendor Name
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={250}
                        placeholder="Hint Text"
                        value={form.vendorName}
                        onChange={(e) => setField("vendorName", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={4}>
                    <Form.Group className="mb-3" controlId="vendorSoftware">
                      <Form.Label className="modalLabel generalFont">
                        Vendor Software
                      </Form.Label>
                      <Form.Control
                        className="modalInput generalFont"
                        type="text"
                        maxLength={250}
                        placeholder="Hint Text"
                        value={form.vendorSoftware}
                        onChange={(e) =>
                          setField("vendorSoftware", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row style={{ padding: "40px 40px 40px 40px" }}>
                  <Form.Group className="mb-3" controlId="comments">
                    <Form.Label className="modalLabel generalFont">
                      Admin Comments
                    </Form.Label>
                    <Form.Control
                      className="modalInput generalFont"
                      as="textarea"
                      maxLength={500}
                      rows={3}
                      value={form.comments}
                      onChange={(e) => setField("comments", e.target.value)}
                    />
                  </Form.Group>
                </Row>
              </Card>
              <Row className="noPaddMarginTop40">
                <Col className="flexCenter">
                  <Button
                    className="addNewBtn flexCenter generalFont"
                    type="submit"
                  >
                    Submit
                  </Button>
                  <Button
                    className="cancelButtonStyle generalFont flexCenter"
                    onClick={cancelForm}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </Box>
        </Row>
      </Container>
      <Modal
        show={showFacilityIdChangeWarningModal}
        onHide={handleRemoveWarningModalClose}
        size="lg"
        centered
      >
        <Modal.Header className="modalHeaderContainer" closeButton>
          <Modal.Title className="modalHeader generalFont">
            Facility ID Has Been Changed
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyContainer">
          <React.Fragment>
            <Form.Label className="modalLabel generalFont">
              Are you sure you want to change the Facility ID?
            </Form.Label>
          </React.Fragment>
        </Modal.Body>
        <Modal.Footer className="modalFooterContainer">
          <Button
            onClick={handleRemoveWarningModalClose}
            className="modalCancelStyle generalFont flexCenter"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmmitChangesToFacilityId}
            className="modalRemoveStyle generalFont flexCenter"
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FacilityForm;
